import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getWishes } from '../api/wishes';
import Pagination from '../components/Pagination';

function formatDate(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString('en-GB', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
  });
}

function groupByMonth(wishes) {
  const groups = {};
  wishes.forEach(w => {
    const date = w.event_date ? new Date(w.event_date) : null;
    const key = date
      ? date.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
      : 'No date';
    if (!groups[key]) groups[key] = [];
    groups[key].push(w);
  });
  return Object.entries(groups);
}

const EVENT_CATEGORIES = ['Theater', 'Show', 'Exhibit'];

function FriendWishes() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [wishes, setWishes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [activeCategory, setActiveCategory] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) { navigate('/signin'); return; }
    fetchWishes(currentPage, activeCategory);
  }, [isAuthenticated, currentPage, activeCategory]);

  const fetchWishes = async (page, category) => {
    setIsLoading(true);
    const token = localStorage.getItem('auth_token');
    try {
      const data = await getWishes(token, null, page, id, { category });
      setWishes(data.wishes || []);
      if (data.meta) {
        setCurrentPage(data.meta.current_page);
        setTotalPages(data.meta.total_pages);
        setTotalCount(data.meta.total_count);
      }
    } catch (err) {
      if (err.response?.status === 403) {
        setError('You are not friends with this user.');
      } else {
        setError('Could not load wishes.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCategoryClick = (cat) => {
    setActiveCategory(prev => prev === cat ? null : cat);
    setCurrentPage(1);
  };

  if (!isAuthenticated) return null;
  if (error) return (
    <div className="wish-list">
      <p className="error">{error}</p>
      <Link to={`/friends/${id}`} className="back-link">← Back to profile</Link>
    </div>
  );

  const groups = groupByMonth(wishes);

  return (
    <div className="wish-list">
      <div className="page-header">
        <button onClick={() => navigate(-1)} className="back-link">← Back</button>
        <h1>Friend's Wishes</h1>
        {!isLoading && <span style={{ color: '#94a3b8', fontSize: '0.875rem' }}>{totalCount} wish{totalCount !== 1 ? 'es' : ''}</span>}
      </div>

      {/* Category filter pills */}
      <div className="wish-filters">
        <div className="wish-filters__group">
          <span className="wish-filters__label">Category</span>
          <div className="wish-filters__pills">
            {EVENT_CATEGORIES.map(cat => (
              <button
                key={cat}
                onClick={() => handleCategoryClick(cat)}
                className={`wish-filter-pill ${activeCategory === cat ? 'wish-filter-pill--active' : ''}`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
        {activeCategory && (
          <button onClick={() => { setActiveCategory(null); setCurrentPage(1); }} className="wish-filters__reset">
            ✕ Clear filter
          </button>
        )}
      </div>

      {isLoading ? (
        <p>Loading...</p>
      ) : wishes.length === 0 ? (
        <p className="wish-list__empty">
          {activeCategory ? `No ${activeCategory} wishes.` : 'No wishes yet.'}
        </p>
      ) : (
        groups.map(([monthYear, items]) => (
          <div key={monthYear} className="moment-group">
            <h2 className="moment-group__heading">{monthYear}</h2>
            <div className="wish-cards">
              {items.map(wish => (
                <div key={wish.id} className="wish-card">
                  {wish.event_url_image && (
                    <div className="wish-card__image-wrapper">
                      <img src={wish.event_url_image} alt={wish.event_name} className="wish-card__image" />
                    </div>
                  )}
                  <div className="wish-card__body">
                    <div className="wish-card__meta">
                      {wish.event_category && <span className="wish-card__category">{wish.event_category}</span>}
                      {wish.event_date && <span className="wish-card__date">📅 {formatDate(wish.event_date)}</span>}
                    </div>
                    <h2 className="wish-card__title">
                      <Link to={`/events/${wish.event_id}`}>{wish.event_name || `Event #${wish.event_id}`}</Link>
                    </h2>
                    {wish.event_description && (
                      <p className="wish-card__description">
                        {wish.event_description.length > 120 ? wish.event_description.slice(0, 120) + '…' : wish.event_description}
                      </p>
                    )}
                    <div className="wish-card__actions">
                      <Link to={`/events/${wish.event_id}`} className="btn btn-primary btn-sm">View event</Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))
      )}

      <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
    </div>
  );
}

export default FriendWishes;
